/**
 * SingleListingDrawer Component for Bay Email Editor
 *
 * Self-contained offcanvas drawer for selecting a single listing property.
 * Uses React Bootstrap Offcanvas instead of Arco Design to support sharing across apps.
 *
 * Features:
 * - Search functionality for MLS# or Address
 * - Pagination with configurable page sizes
 * - Single selection mode with radio buttons
 * - Integration with ListingsDataGrid component
 * - Uses Bay UI components (BaySearchInput, BayPagination) for consistency
 *
 * Dependencies:
 * - react-bootstrap (Offcanvas, Button)
 * - ka-table (for data grid)
 * - bay-ui components (BaySearchInput, BayPagination)
 *
 * Note: This component is designed to work across multiple applications:
 * - Client App (this codebase)
 * - DSA App
 * - Future applications
 */

import React, { useCallback, useState, useEffect, useRef, useMemo } from 'react';
import { Button, Offcanvas } from 'react-bootstrap';
import { BsHouse, BsBuilding, BsHouses, BsPinMapFill } from 'react-icons/bs';
import { t } from '@bay-email/core';
import { Tooltip, useEditorProps } from '@bay-email/editor';
import { DataType, SortDirection, SortingMode } from 'ka-table/enums';
import ListingsDataGrid from '@bay-email/extensions/components/ListingsDataGrid';
import TitleCell from '@bay-email/extensions/components/ListingsDataGrid/TitleCell';
import BayPagination from '@bay-ui/BayPagination';
import BayViews from '@bay-ui/BayViews';
import { useTruncateCheck } from '@bay-ui/utilities/hook';
import ListingFilterDrawer from './ListingFilterDrawer';
import './SingleListingDrawer.scss';

interface ListingItem {
	id: string;
	title?: string;
	address?: string;
	address1?: string;
	city?: string;
	state?: string;
	zip?: string;
	list_price?: number;
	beds?: number;
	baths?: number;
	sq_foot?: number;
	image_data?: string[];
	image?: string;
	mls?: string;
	dateListed?: string;
}

interface ListingFiltersPayload {
	startdate?: string;
	enddate?: string;
	propertytype?: string[];
	pricemin?: string;
	pricemax?: string;
	status?: string[];
}

interface ListingSortPayload {
	sortBy?: string;
	orderDirection?: 'ASC' | 'DESC';
}

interface SelectOption {
	label: string;
	value: string;
}

const ALL_PROPERTY_TYPE_VALUES = ['residential', 'multifamily', 'commercial', 'land'];

interface SingleListingDrawerProps {
	listings: ListingItem[];
	selectedListing?: ListingItem | null;
	total: number;
	size: number;
	loading: boolean;
	onSelect: (selectedListing: ListingItem | null) => void;
	onPaginationChange: (params: {
		page: number;
		size: number;
		keyword?: string;
		filters?: ListingFiltersPayload;
		sortBy?: string;
		orderDirection?: 'ASC' | 'DESC';
	}) => void;
	children: React.ReactNode;
	pagination?: any;
	disabled?: boolean;
	openSignal?: number;
	/**
	 * The drawer became visible. Panels use this to defer loading the search
	 * grid until it is actually going to be looked at — see the note on the
	 * effect in AttachedListingSection.
	 */
	onOpen?: () => void;
	setActiveView?: (activeView: {
		id: 'mylistings' | 'companylistings';
		page: number;
		size: number;
	}) => void;
}

// Helper function to format date
const formatDateListed = (dateString?: string) => {
	if (!dateString || dateString === '-' || dateString === 'Invalid Date') return '-';
	try {
		let date: Date;
		// Handle already formatted date like "09/06/2024 at 4:09:06 PM"
		if (dateString.includes(' at ')) {
			const datePart = dateString.split(' at ')[0];
			const [month, day, year] = datePart.split('/');
			date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
		}
		// Handle YYYYMMDDHHMMSS format (e.g., 20240906160906)
		else if (/^\d{14}$/.test(dateString)) {
			const year = parseInt(dateString.substring(0, 4));
			const month = parseInt(dateString.substring(4, 6)) - 1; // Month is 0-indexed
			const day = parseInt(dateString.substring(6, 8));
			date = new Date(year, month, day);
		} else {
			date = new Date(dateString);
		}
		if (isNaN(date.getTime())) return '-';
		return date.toLocaleDateString('en-US', {
			month: 'short',
			day: 'numeric',
			year: 'numeric',
		});
	} catch (e) {
		return '-';
	}
};

const getPropertyTypeIcon = (type?: string) => {
	switch (type) {
		case 'land':
			return <BsPinMapFill className='me-2' size={16} />;
		case 'multifamily':
			return <BsHouses className='me-2' size={16} />;
		case 'commercial':
			return <BsBuilding className='me-2' size={16} />;
		default:
			return <BsHouse className='me-2' size={16} />;
	}
};

const ListingAgentCell: React.FC<{ name?: string }> = ({ name }) => {
	const displayName = name || '-';
	const [nameRef, isNameTruncated] = useTruncateCheck([displayName]) as [React.RefObject<HTMLDivElement>, boolean];

	return (
		<Tooltip disabled={!isNameTruncated} placement='top' content={displayName}>
			<div ref={nameRef} className='text-capitalize text-truncate'>
				{displayName}
			</div>
		</Tooltip>
	);
};

// Column definitions using ka-table pattern
const getListingColumns = (sortConfig: ListingSortPayload = {}) => {
	const isPriceSorted = sortConfig.sortBy === 'list_price';
	const isDateListedSorted = sortConfig.sortBy === 'adddatetime';
	const getSortDirection = (isSorted: boolean) => {
		if (!isSorted) return SortDirection.Descend;
		return sortConfig.orderDirection === 'ASC' ? SortDirection.Ascend : SortDirection.Descend;
	};

	return {
		sortingMode: SortingMode.SingleRemote,
		columns: [
			{
				key: 'address',
				width: 276,
				title: 'ADDRESS & MLS #',
				dataType: DataType.String,
				customStyles: {
					paddingLeft: 0,
					paddingRight: 0,
					width: 276,
				},
				customComponent: ({ rowData }: any) => {
					return (
						<TitleCell
							data={rowData}
						/>
					);
				},
			},
			{
				key: 'status',
				width: 140,
				title: 'STATUS',
				dataType: DataType.String,
				customComponent: ({ rowData }: any) => {
					return (
						<div className='text-capitalize'>{rowData?.status || '-'}</div>
					);
				},
			},
			{
				key: 'price',
				width: 140,
				title: 'PRICE',
				dataType: DataType.String,
				sortBy: 'list_price',
				sortDirection: getSortDirection(isPriceSorted),
				customComponent: ({ rowData }: any) => {
					return (
						<div>${new Intl.NumberFormat('en-US').format(rowData?.list_price || 0)}</div>
					);
				},
			},
			{
				key: 'propertyType',
				width: 140,
				title: 'PROPERTY TYPE',
				dataType: DataType.String,
				customComponent: ({ rowData }: any) => {
					return (
						<div className='text-capitalize d-flex align-items-center'>
							{getPropertyTypeIcon(rowData?.propertyType?.toLowerCase())}
							<span>{rowData?.propertyType || '-'}</span>
						</div>
					);
				},
			},
			{
				key: 'listingAgent',
				width: 140,
				title: 'LISTING AGENT',
				dataType: DataType.String,
				customComponent: ({ rowData }: any) => {
					return (
						<ListingAgentCell name={rowData?.agentName} />
					);
				},
			},
			{
				key: 'dateListed',
				width: 140,
				title: 'DATE LISTED',
				dataType: DataType.String,
				sortBy: 'adddatetime',
				sortDirection: getSortDirection(isDateListedSorted),
				customComponent: ({ rowData }: any) => {
					return <div>{formatDateListed(rowData?.dateListed)}</div>;
				},
			},
		],
		rowKeyField: 'id',
	};
};

const getInitialViews = (canViewCompanyListings = true) => [
	{
		id: 'mylistings',
		viewname: 'My Listings',
		active: true,
		paginationData: {
			page: 1,
			size: 10,
		},
	},
	...(canViewCompanyListings
		? [
				{
					id: 'companylistings',
					viewname: 'Company Listings',
					active: false,
					paginationData: {
						page: 1,
						size: 10,
					},
				},
			]
		: []),
];

export const SingleListingDrawer: React.FC<SingleListingDrawerProps> = props => {
	const statusOptions = [
		{ label: 'Active', value: 'active' },
		{ label: 'Sold', value: 'sold' },
		{ label: 'Withdrawn', value: 'withdrawn' },
		{ label: 'Expired', value: 'expired' },
		{ label: 'Rented', value: 'rented' },
		{ label: 'Leased', value: 'leased' },
		{ label: 'Off Market', value: 'off market' },
		{ label: 'Canceled', value: 'canceled' },
		{ label: 'Coming Soon', value: 'coming soon' },
		{ label: 'Under Contract', value: 'under contract' },
	];
	const propertyTypeOptions = [
		{ label: 'Residential', value: 'residential' },
		{ label: 'Land', value: 'land' },
		{ label: 'Multi-family', value: 'multifamily' },
		{ label: 'Commercial', value: 'commercial' },
	];
	const [visible, setVisible] = useState<boolean>(false);
	const [tableProps, setTableProps] = useState<any>(null);
	const [selectedRows, setSelectedRows] = useState<string[]>([]);
	const [searchValue, setSearchValue] = useState<string>('');
	const [priceMin, setPriceMin] = useState<string>('');
	const [priceMax, setPriceMax] = useState<string>('');
	const [startDate, setStartDate] = useState<string>('');
	const [endDate, setEndDate] = useState<string>('');
	const [selectedStatus, setSelectedStatus] = useState<SelectOption[]>([]);
	const [selectedPropertyType, setSelectedPropertyType] = useState<SelectOption[]>([]);
	const [isFilterExpanded, setIsFilterExpanded] = useState<boolean>(false);
	const [sortConfig, setSortConfig] = useState<ListingSortPayload>({});
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [pageSize, setPageSize] = useState<number>(props.size || 10);
	const { canViewCompanyListings } = useEditorProps();
	const [viewData, setViewData] = useState(() => getInitialViews(canViewCompanyListings));
	const onPaginationChangeRef = useRef(props.onPaginationChange);
	const searchValueRef = useRef(searchValue);
	const pageSizeRef = useRef(pageSize);
	const sortConfigRef = useRef(sortConfig);

	const filterCount = useMemo(() => {
		let count = 0;
		if (priceMin?.trim() || priceMax?.trim()) count += 1;
		if (startDate?.trim() || endDate?.trim()) count += 1;
		if (selectedStatus?.length) count += 1;
		if (selectedPropertyType?.length) count += 1;
		return count;
	}, [priceMin, priceMax, startDate, endDate, selectedStatus, selectedPropertyType]);

	const getActiveFilters = useCallback((): ListingFiltersPayload => {
		const filters: ListingFiltersPayload = {};
		if (startDate?.trim()) filters.startdate = startDate.trim();
		if (endDate?.trim()) filters.enddate = endDate.trim();
		if (priceMin?.trim()) filters.pricemin = priceMin.trim();
		if (priceMax?.trim()) filters.pricemax = priceMax.trim();
		if (selectedStatus?.length) filters.status = selectedStatus.map(option => option.value);
		filters.propertytype = selectedPropertyType?.length
			? selectedPropertyType.map(option => option.value)
			: ALL_PROPERTY_TYPE_VALUES;
		return filters;
	}, [endDate, priceMax, priceMin, selectedPropertyType, selectedStatus, startDate]);

	const pageSizes = [10, 25, 50, 100];

	// Keep refs updated with latest values
	useEffect(() => {
		onPaginationChangeRef.current = props.onPaginationChange;
	}, [props.onPaginationChange]);

	useEffect(() => {
		searchValueRef.current = searchValue;
	}, [searchValue]);

	useEffect(() => {
		pageSizeRef.current = pageSize;
	}, [pageSize]);

	useEffect(() => {
		sortConfigRef.current = sortConfig;
	}, [sortConfig]);

	// Initialize table props
	useEffect(() => {
		setTableProps(getListingColumns(sortConfig));
	}, [sortConfig]);

	// Update selected rows when selectedListing changes
	useEffect(() => {
		if (props.selectedListing) {
			setSelectedRows([props.selectedListing.id]);
		} else {
			setSelectedRows([]);
		}
	}, [props.selectedListing]);

	// openSignal is an EDGE, not a level. It used to open on any value >= 1, so a
	// remount with the signal still sitting at its last value re-opened the drawer
	// on its own — click Apply, the drawer closes, the panel re-renders, and it
	// comes straight back. Adopting the current value on mount without acting
	// makes only a genuine bump open it.
	// Fires for every route into the drawer — the trigger, and the programmatic
	// openSignal below — because both land on `visible`.
	const onOpenRef = useRef(props.onOpen);
	useEffect(() => {
		onOpenRef.current = props.onOpen;
	}, [props.onOpen]);
	useEffect(() => {
		if (visible) onOpenRef.current?.();
	}, [visible]);

	const handledOpenSignalRef = useRef<number | null>(null);
	useEffect(() => {
		const signal = typeof props.openSignal === 'number' ? props.openSignal : 0;
		if (handledOpenSignalRef.current === null) {
			handledOpenSignalRef.current = signal;
			return;
		}
		if (props.disabled || signal < 1 || signal === handledOpenSignalRef.current) {
			return;
		}
		handledOpenSignalRef.current = signal;
		setVisible(true);
	}, [props.disabled, props.openSignal]);

	const handleSelectionChange = useCallback(
		(selected: string[]) => {
			setSelectedRows(selected);
		},
		[props],
	);

	const handleConfirm = useCallback(() => {
		if (selectedRows && selectedRows.length > 0) {
			const selectedItem = props.listings.find(listing => listing.id === selectedRows[0]);
			if (selectedItem) {
				props.onSelect(selectedItem);
			}
		} else {
			props.onSelect(null);
		}
		setVisible(false);
	}, [props, selectedRows]);

	const handleCancel = useCallback(() => {
		setVisible(false);
		if (!props?.selectedListing?.id) {
			setSelectedRows([]);
		}
	}, [props]);

	const handleSearch = useCallback(
		(value: string) => {
			setSearchValue(value);
			setCurrentPage(1);
			props.onPaginationChange({
				page: 1,
				size: pageSize,
				keyword: value,
				filters: getActiveFilters(),
				sortBy: sortConfigRef.current.sortBy,
				orderDirection: sortConfigRef.current.orderDirection,
			});
			handleViewPaginationChange(pageSize, 1);
		},
		[getActiveFilters, pageSize, props],
	);

	const handlePageChange = useCallback((pageNumber: number, newPageSize: number) => {
		// BayPagination calls this when page or size changes - always update
		const isPageSizeChange = newPageSize !== pageSizeRef.current;

		// Always update state first
		setPageSize(newPageSize);

		// Determine which page to use
		const targetPage = isPageSizeChange ? 1 : pageNumber;

		// Always update current page
		setCurrentPage(targetPage);

		// Always call onPaginationChange with latest values
		onPaginationChangeRef.current({
			page: targetPage,
			size: newPageSize,
			keyword: searchValueRef.current,
			filters: getActiveFilters(),
			sortBy: sortConfigRef.current.sortBy,
			orderDirection: sortConfigRef.current.orderDirection,
		});
		handleViewPaginationChange(newPageSize, targetPage);
	}, [getActiveFilters]);

	const handleViewPaginationChange = useCallback(
		(newPageSize: number, targetPage: number) => {
			setViewData(prev => {
				return prev.map(view => ({
					...view,
					paginationData: view.active
						? { page: targetPage, size: newPageSize }
						: view.paginationData,
				}));
			});
		},
		[],
	);

	const handleSortChange = useCallback((nextSort: { sortBy: string; orderDirection: 'ASC' | 'DESC' }) => {
		setSortConfig(nextSort);
		setCurrentPage(1);
		onPaginationChangeRef.current({
			page: 1,
			size: pageSizeRef.current,
			keyword: searchValueRef.current,
			filters: getActiveFilters(),
			sortBy: nextSort.sortBy,
			orderDirection: nextSort.orderDirection,
		});
		handleViewPaginationChange(pageSizeRef.current, 1);
	}, [getActiveFilters, handleViewPaginationChange]);

	const handleViewChange = useCallback(
		(views: any[]) => {
			const activeView = views?.find(v => v?.active);
			setViewData(views);

			if (!activeView) return;

			const { id, paginationData = {} } = activeView;
			const { page = 1, size = 10 } = paginationData;

			props.setActiveView?.({ id, page, size });
			setPageSize(size);
			setCurrentPage(page);
			setSearchValue('');
			setPriceMin('');
			setPriceMax('');
			setStartDate('');
			setEndDate('');
			sortConfigRef.current = {};
			setSortConfig({});
			setSelectedStatus([]);
			setSelectedPropertyType([]);
			setIsFilterExpanded(false);
		},
		[props.setActiveView],
	);

	if (!tableProps) return null;

	return (
		<>
			<Offcanvas
				show={visible}
				onHide={handleCancel}
				placement='end'
				style={{ width: '1080px' }}
				backdrop={true}
				scroll={false}
				className='single-listing-drawer'
			>
				<Offcanvas.Header
					closeButton
					className='border-bottom px-5 py-4'
				>
					<Offcanvas.Title>
						<h1 className='m-0'>{t('Select a Property')}</h1>
					</Offcanvas.Title>
				</Offcanvas.Header>
				<Offcanvas.Body className='d-flex flex-column h-100 p-0'>
					<div
						className='d-flex flex-column gap-4 flex-fill px-5 pb-4'
						style={{ minHeight: 0 }}
					>
						<div id='listings-view'>
							<BayViews
								customModalContent={true}
								isAllowRestoreDefault={false}
								t={t}
								viewData={viewData}
								showDivider={false}
								setActiveView={handleViewChange}
							/>
						</div>
						<ListingFilterDrawer
							loading={props.loading}
							filterCount={filterCount}
							defaultExpanded={isFilterExpanded}
							searchValue={searchValue}
							onSearch={handleSearch}
							onSearchValueChange={setSearchValue}
							onFilterButtonClick={setIsFilterExpanded}
							priceMin={priceMin}
							priceMax={priceMax}
							onPriceMinChange={setPriceMin}
							onPriceMaxChange={setPriceMax}
							startDate={startDate}
							endDate={endDate}
							onStartDateChange={setStartDate}
							onEndDateChange={setEndDate}
							statusOptions={statusOptions}
							selectedStatus={selectedStatus}
							onStatusChange={setSelectedStatus}
							propertyTypeOptions={propertyTypeOptions}
							selectedPropertyType={selectedPropertyType}
							onPropertyTypeChange={setSelectedPropertyType}
							onClose={() => {}}
							onClearAll={() => {
								setPriceMin('');
								setPriceMax('');
								setStartDate('');
								setEndDate('');
								setSelectedStatus([]);
								setSelectedPropertyType([]);
								setIsFilterExpanded(false);
								setCurrentPage(1);
								onPaginationChangeRef.current({
									page: 1,
									size: pageSizeRef.current,
									keyword: searchValueRef.current,
									filters: {
										startdate: '',
										enddate: '',
										propertytype: ALL_PROPERTY_TYPE_VALUES,
										pricemin: '',
										pricemax: '',
										status: [],
									},
									sortBy: sortConfigRef.current.sortBy,
									orderDirection: sortConfigRef.current.orderDirection,
								});
								handleViewPaginationChange(pageSizeRef.current, 1);
							}}
							onApplyFilter={() => {
								setIsFilterExpanded(false);
								setCurrentPage(1);
								onPaginationChangeRef.current({
									page: 1,
									size: pageSizeRef.current,
									keyword: searchValueRef.current,
									filters: getActiveFilters(),
									sortBy: sortConfigRef.current.sortBy,
									orderDirection: sortConfigRef.current.orderDirection,
								});
								handleViewPaginationChange(pageSizeRef.current, 1);
							}}
						/>

						<div
							className='flex-fill overflow-auto'
							style={{ minHeight: 0 }}
						>
							<ListingsDataGrid
								data={props.listings}
								enableRowSelection={true}
								selectionMode='single'
								initTableProps={tableProps}
								loading={props.loading}
								setSelectedData={handleSelectionChange}
								onSortChange={handleSortChange}
								t={(key: string) => key}
								selectedListing={selectedRows}
								hasSearch={!!searchValue?.trim()}
								hasFilters={filterCount > 0}
							/>
						</div>

						{props.listings && props.listings.length > 0 && (
							<div className='bg-white d-flex align-items-center justify-content-center'>
								<BayPagination
									pageSizeOptions={pageSizes}
									selectedPageSize={pageSize}
									totalRowCount={props.total}
									totalPages={Math.ceil(props.total / pageSize)}
									activePage={currentPage}
									handlePageChange={handlePageChange}
									disabled={props.loading}
									t={(key: string) => key}
									pageSizeSelectorLabel={t('Rows per page')}
									isPageSizeDropDown={true}
								/>
							</div>
						)}
					</div>

					<div className='p-3 d-flex justify-content-between gap-2 border-top'>
						<Button
							variant='outline-secondary'
							onClick={handleCancel}
						>
							Cancel
						</Button>
						<Button
							variant='primary'
							onClick={handleConfirm}
							disabled={!selectedRows.length}
						>
							Apply
						</Button>
					</div>
				</Offcanvas.Body>
			</Offcanvas>
			<div
				onClick={() => !props.disabled && setVisible(true)}
				style={{ cursor: props.disabled ? 'not-allowed' : 'pointer' }}
			>
				{props.children}
			</div>
		</>
	);
};
